import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi, setTokens, clearTokens, getAccessToken, User } from '@/lib/api';
import toast from 'react-hot-toast';
import { AxiosError } from 'axios';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<{ requiresTotp?: boolean; tempToken?: string }>;
  verifyTotp: (token: string, tempToken: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

// Helper to extract error message from axios error
// Handles multiple API response formats:
// - { error: { message: "..." } }
// - { error: "..." }
// - { message: "..." }
// - Plain string response
function getErrorMessage(error: unknown): string {
  if (error instanceof AxiosError) {
    const data = error.response?.data;
    
    if (data) {
      // Format: { error: { message: "..." } }
      if (typeof data.error === 'object' && data.error?.message) {
        return data.error.message;
      }
      // Format: { error: "..." }
      if (typeof data.error === 'string') {
        return data.error;
      }
      // Format: { message: "..." }
      if (typeof data.message === 'string') {
        return data.message;
      }
      // Plain string response
      if (typeof data === 'string') {
        return data;
      }
    }
    
    // Fall back to axios message
    return error.message || 'An error occurred';
  }
  
  if (error instanceof Error) {
    return error.message;
  }
  
  return 'An error occurred';
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = getAccessToken();
      if (token) {
        try {
          const response = await authApi.me();
          if (response.success && response.data) {
            setUser(response.data);
          } else {
            clearTokens();
          }
        } catch {
          clearTokens();
        }
      }
      setIsLoading(false);
    };
    
    checkAuth();
  }, []);

  const login = async (username: string, password: string) => {
    try {
      const response = await authApi.login(username, password);
      
      if (!response.success) {
        throw new Error(response.error?.message || 'Login failed');
      }
      
      // Check if TOTP is required
      if (response.data?.requiresTotp) {
        return { requiresTotp: true, tempToken: response.data.tempToken };
      }
      
      // No TOTP - complete login
      if (response.data?.accessToken) {
        setTokens(response.data.accessToken, response.data.refreshToken);
        
        // Fetch full user data from /auth/me to ensure isAdmin etc are populated
        const meResponse = await authApi.me();
        if (meResponse.success && meResponse.data) {
          setUser(meResponse.data);
        } else {
          setUser(response.data.user);
        }
        
        toast.success(`Welcome back, ${response.data.user.fullName}`);
        navigate('/');
      }
      
      return {};
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      throw error;
    }
  };

  const verifyTotp = async (token: string, tempToken: string) => {
    try {
      const response = await authApi.verifyTotp(token, tempToken);
      
      if (!response.success) {
        throw new Error(response.error?.message || 'TOTP verification failed');
      }
      
      if (response.data) {
        setTokens(response.data.accessToken, response.data.refreshToken);
        
        // Fetch full user data from /auth/me to ensure isAdmin etc are populated
        const meResponse = await authApi.me();
        if (meResponse.success && meResponse.data) {
          setUser(meResponse.data);
        } else {
          setUser(response.data.user);
        }
        
        toast.success(`Welcome back, ${response.data.user.fullName}`);
        navigate('/');
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } finally {
      setUser(null);
      navigate('/login');
      toast.success('Logged out successfully');
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        verifyTotp,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
